import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  AxiosRequestHeaders,
} from "axios";
import { getCurrentEnvironment, getBaseUrl } from "../config";

const environment = getCurrentEnvironment();
console.log("Environment:", environment);
const baseUrl = getBaseUrl(environment);
console.log("Base URL:", baseUrl);

const apiClient: AxiosInstance = axios.create({
  baseURL: baseUrl,
});

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

const notifyAuthChange = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-change"));
  }
};

const getStoredAccessToken = () => localStorage.getItem("token");
const getStoredRefreshToken = () => localStorage.getItem("refreshToken");

const setStoredTokens = (accessToken: string, refreshToken?: string) => {
  localStorage.setItem("token", accessToken);
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }
  notifyAuthChange();
};

const clearStoredTokens = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("companyName");
  localStorage.removeItem("openMenu");
  notifyAuthChange();
};

const applyAccessTokenToRequest = (
  config: RetryableRequestConfig,
  accessToken: string,
) => {
  if (!config.headers) {
    config.headers = {} as AxiosRequestHeaders;
  }
  (config.headers as any)["x-api-token"] = accessToken;
  (config.headers as any)["Authorization"] = `Bearer ${accessToken}`;

  if (config.data instanceof FormData) {
    if (config.data.has("token")) {
      config.data.delete("token");
    }
    config.data.append("token", accessToken);
  } else if (config.data && typeof config.data === "object") {
    if ("token" in config.data) {
      (config.data as any).token = accessToken;
    }
  } else if (typeof config.data === "string") {
    try {
      const parsed = JSON.parse(config.data);
      if (parsed && typeof parsed === "object" && "token" in parsed) {
        parsed.token = accessToken;
        config.data = JSON.stringify(parsed);
      }
    } catch {
      // ignore invalid JSON
    }
  }

  if (config.params && (config.params as any).token) {
    (config.params as any).token = accessToken;
  }
};

let refreshPromise: Promise<{
  accessToken: string;
  refreshToken?: string;
} | null> | null = null;

const refreshTokens = async () => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  refreshPromise = axios
    .post(`${baseUrl}user/refresh`, { refreshToken })
    .then((response) => {
      const { accessToken, refreshToken: newRefreshToken } =
        response.data || {};
      if (!accessToken) return null;
      setStoredTokens(accessToken, newRefreshToken);
      return { accessToken, refreshToken: newRefreshToken };
    })
    .catch(() => {
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const createApiClient = (endpoint: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: `${baseUrl}${endpoint}`,
  });

  // Add request interceptor to inject token from localStorage
  instance.interceptors.request.use(
    (config) => {
      const token = getStoredAccessToken();
      if (token) {
        if (!config.headers) {
          config.headers = {} as AxiosRequestHeaders;
        }
        // Add token to x-api-token header for all requests (works locally)
        (config.headers as any)["x-api-token"] = token;
        (config.headers as any)["Authorization"] = `Bearer ${token}`;

        // For file uploads (FormData): also add token to request body
        // This ensures token survives reverse proxy header stripping on production
        if (config.data instanceof FormData) {
          // Only add token if it's not already in the FormData
          if (!config.data.has("token")) {
            config.data.append("token", token);
          }
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | RetryableRequestConfig
        | undefined;
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const status = error.response?.status;
      const requestUrl = originalRequest.url || "";
      const isAuthRequest =
        requestUrl.includes("login") || requestUrl.includes("refresh");

      if (status === 401 && !originalRequest._retry && !isAuthRequest) {
        originalRequest._retry = true;

        const refreshed = await refreshTokens();
        if (refreshed?.accessToken) {
          applyAccessTokenToRequest(originalRequest, refreshed.accessToken);
          return instance(originalRequest);
        }

        clearStoredTokens();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export default apiClient;
